import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GoogleAddressService, Maps } from '../../services/google-address.service';

type Components = google.maps.places.PlaceResult['address_components'];

@Component({
  selector: 'app-google-address-textfield',
  templateUrl: './google-address-textfield.component.html',
  styleUrls: ['./google-address-textfield.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => GoogleAddressTextFieldComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GoogleAddressTextFieldComponent implements OnChanges, ControlValueAccessor {
  // INPUTS
  @Input() input: string = '';
  @Input() label: string | null = null;
  @Input() placeholder!: string;

  @Input() isError: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isAlwaysNeedClean?: boolean = true;

  // OUTPUTS
  @Output() onValueChange = new EventEmitter<any>();
  @Output() onFocusOut = new EventEmitter<Event>();

  // REFS
  @ViewChild('googleAddressSearch') searchElementRef!: ElementRef;

  // LOCAL var`s
  private googleCoordinates: any;

  constructor(
    public _googleAddressService: GoogleAddressService,
    private ngZone: NgZone,
  ) {
    _googleAddressService.api.then((maps) => {
      this.initAutocomplete(maps);
    });
  }

  public ngOnChanges(changes: any): void {
    if (changes?.input?.currentValue.trim() !== '' && this.isAlwaysNeedClean) {
      this.input = '';
    }
  }

  public onChange = (_data: any) => {};
  public onTouch = (_: any) => {};

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public writeValue(value: string) {
    this.input = value;
    this.onChange(value);
  }

  public onchange(event: any) {
    this.input = event;
    this.onChange({ address: event });
    this.onValueChange.emit({ address: event });
  }

  public focusOutInput(event: any) {
    this.onFocusOut.emit(event);
  }

  private initAutocomplete(maps: Maps) {
    const autocomplete = new maps.places.Autocomplete(this.searchElementRef?.nativeElement, {
      componentRestrictions: { country: ['ua'] },
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        this.onPlaceChange(autocomplete.getPlace());
      });
    });
  }

  private onPlaceChange(place: google.maps.places.PlaceResult) {
    const location = this.locationFromPlace(place);

    this.input = place.formatted_address as string;
    const payload = {
      address: place?.formatted_address,
      city: location?.cityName,
      state: location?.stateCode,
      country: location?.countryName,
      zipcode: location?.postal_code,
      latitude: this.googleCoordinates?.latitude,
      longitude: this.googleCoordinates?.longitude,
    };

    this.onChange(payload);
    this.onValueChange.emit(payload);
  }

  private locationFromPlace(place: google.maps.places.PlaceResult) {
    const components = place.address_components;
    if (components === undefined) {
      return null;
    }

    const areaLevel3 = this.getShort(components, 'administrative_area_level_3');
    const locality = this.getLong(components, 'locality');

    const cityName = locality || areaLevel3;
    const countryName = this.getLong(components, 'country');
    const countryCode = this.getShort(components, 'country');
    const stateCode = this.getShort(components, 'administrative_area_level_1');
    const postal_code = this.getShort(components, 'postal_code');
    const name = place.name !== cityName ? place.name : null;

    const coordinates = {
      latitude: place.geometry?.location?.lat(),
      longitude: place.geometry?.location?.lng(),
    };
    const bounds = place.geometry?.viewport?.toJSON();
    this.googleCoordinates = coordinates;

    return {
      name,
      cityName,
      countryName,
      countryCode,
      stateCode,
      bounds,
      coordinates,
      postal_code,
    };
  }

  private getComponent(components: Components, name: string) {
    return components?.filter((component) => component.types[0] === name)[0];
  }

  private getLong(components: Components, name: string) {
    const component = this.getComponent(components, name);
    return component && component.long_name;
  }

  private getShort(components: Components, name: string) {
    const component = this.getComponent(components, name);
    return component && component.short_name;
  }
}
